import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import {
  Building2, Video, MapPin, Clock, Wifi, Smartphone,
  ExternalLink, Calendar, ChevronRight, Shield
} from 'lucide-react';

const HEALTHPLIX_BOOKING_URL = 'https://book.healthplix.com/dr-akshay-kawedia-pulmonologist-pune-pune';

const AppointmentBooking = () => {
  const [selected, setSelected] = useState(null);

  const handleBook = () => {
    window.open(HEALTHPLIX_BOOKING_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <Card className="border-0 shadow-xl dark:shadow-slate-900/50 overflow-hidden" data-testid="appointment-booking-card">
      <CardContent className="p-0">
        {/* Header */}
        <div className="px-6 sm:px-8 pt-6 sm:pt-8 pb-5">
          <div className="flex items-center gap-3 mb-1">
            <Calendar className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            <h3 className="text-xl font-bold text-gray-900 dark:text-slate-50">
              Book an Appointment
            </h3>
          </div>
          <p className="text-sm text-gray-500 dark:text-slate-400 ml-9">Choose your preferred consultation type</p>
        </div>

        {/* Type Selector */}
        <div className="px-6 sm:px-8 pb-5">
          <div className="grid grid-cols-2 gap-3">
            {/* In-Person */}
            <button
              type="button"
              onClick={() => setSelected('in_person')}
              data-testid="type-in-person"
              className={`relative flex flex-col items-center gap-3 p-5 rounded-xl border-2 transition-all duration-200 cursor-pointer ${
                selected === 'in_person'
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 dark:border-blue-400 shadow-md'
                  : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700'
              }`}
            >
              {selected === 'in_person' && (
                <span className="absolute top-2 right-2 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </span>
              )}
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                selected === 'in_person' ? 'bg-blue-100 dark:bg-blue-800/50' : 'bg-gray-100 dark:bg-slate-700'
              }`}>
                <Building2 className={`w-6 h-6 ${selected === 'in_person' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-slate-400'}`} />
              </div>
              <span className={`text-sm font-semibold ${selected === 'in_person' ? 'text-blue-700 dark:text-blue-300' : 'text-gray-600 dark:text-slate-300'}`}>
                In-Person
              </span>
              <span className={`text-xs ${selected === 'in_person' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-slate-500'}`}>
                Clinic Visit
              </span>
            </button>

            {/* Online Video */}
            <button
              type="button"
              onClick={() => setSelected('online_video')}
              data-testid="type-online-video"
              className={`relative flex flex-col items-center gap-3 p-5 rounded-xl border-2 transition-all duration-200 cursor-pointer ${
                selected === 'online_video'
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 dark:border-blue-400 shadow-md'
                  : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700'
              }`}
            >
              {selected === 'online_video' && (
                <span className="absolute top-2 right-2 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </span>
              )}
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                selected === 'online_video' ? 'bg-blue-100 dark:bg-blue-800/50' : 'bg-gray-100 dark:bg-slate-700'
              }`}>
                <Video className={`w-6 h-6 ${selected === 'online_video' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-slate-400'}`} />
              </div>
              <span className={`text-sm font-semibold ${selected === 'online_video' ? 'text-blue-700 dark:text-blue-300' : 'text-gray-600 dark:text-slate-300'}`}>
                Online Video
              </span>
              <span className={`text-xs ${selected === 'online_video' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-slate-500'}`}>
                Video Call
              </span>
            </button>
          </div>
        </div>

        {/* Dynamic Info Panel */}
        {selected && (
          <div className="px-6 sm:px-8 pb-5">
            {selected === 'in_person' ? (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl p-5 space-y-4" data-testid="inperson-info">
                <div className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  <span className="font-semibold text-blue-800 dark:text-blue-300">Clinic Consultation</span>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-blue-800 dark:text-blue-200">
                      <p className="font-medium">Clinic Timings</p>
                      <p>Monday – Saturday: 5:00 PM – 8:00 PM</p>
                      <p>Sunday: Closed</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      Kawedia Hospital & Clinic, 2144, MG Road, opp. Poolgate Police Chowky, New Modikhana, Pune 411001
                    </p>
                  </div>
                </div>
                <div className="bg-blue-100/60 dark:bg-blue-800/30 rounded-lg p-3">
                  <p className="text-xs text-blue-700 dark:text-blue-300 font-medium">
                    Please arrive 10–15 minutes before your scheduled appointment.
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl p-5 space-y-4" data-testid="online-info">
                <div className="flex items-center gap-2">
                  <Video className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  <span className="font-semibold text-blue-800 dark:text-blue-300">Online Video Consultation</span>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-blue-800 dark:text-blue-200">
                      <p className="font-medium">Online Consultation Timings</p>
                      <p>Monday – Saturday: 9:00 PM – 9:30 PM</p>
                      <p>Sunday: Closed</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Shield className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      Your consultation will be conducted securely through HealthPlix. Please ensure you have a stable internet connection and a camera-enabled device.
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <Wifi className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      Wi-Fi connection recommended for best video quality.
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <Smartphone className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      Use a charged smartphone, tablet, or laptop with working camera & microphone.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Note + CTA */}
        <div className="px-6 sm:px-8 pb-6 sm:pb-8 space-y-4">
          {selected && (
            <p className="text-xs text-gray-500 dark:text-slate-400 text-center bg-gray-50 dark:bg-slate-800 rounded-lg p-3">
              You will be redirected to HealthPlix to complete your booking. Please select
              <strong> {selected === 'in_person' ? 'In-Person' : 'Online Video'} Consultation</strong> on the booking page.
            </p>
          )}

          <Button
            onClick={handleBook}
            disabled={!selected}
            data-testid="book-appointment-btn"
            className={`w-full py-6 text-sm font-semibold rounded-xl transition-all duration-200 ${
              selected
                ? 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-400 text-white hover:shadow-lg hover:-translate-y-0.5'
                : 'bg-gray-200 dark:bg-slate-700 text-gray-400 dark:text-slate-500 cursor-not-allowed'
            }`}
          >
            <Calendar className="w-5 h-5 mr-2" />
            Book Appointment
            {selected && <ExternalLink className="w-4 h-4 ml-2" />}
          </Button>

          <p className="text-center text-xs text-gray-400 dark:text-slate-500">
            Powered by <span className="font-medium text-gray-500 dark:text-slate-400">HealthPlix</span>
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default AppointmentBooking;
